import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DropdownMenu.scss";

const MENU_WIDTH = 268;
const MENU_ESTIMATED_HEIGHT = 320;
const VIEWPORT_PADDING = 8;

function resolveTriggerEl(trigger) {
  if (!trigger) return null;
  if (trigger instanceof HTMLElement) return trigger;
  if (trigger.current instanceof HTMLElement) return trigger.current;
  return null;
}

function computeMenuPosition(triggerEl, align, menuWidth, menuHeight) {
  if (!triggerEl) {
    return { top: VIEWPORT_PADDING, left: VIEWPORT_PADDING, ready: false };
  }

  const rect = triggerEl.getBoundingClientRect();
  if (!rect.width && !rect.height) {
    return { top: VIEWPORT_PADDING, left: VIEWPORT_PADDING, ready: false };
  }

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  let left = align === "end" ? rect.right - menuWidth : rect.left;

  if (left + menuWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - menuWidth - VIEWPORT_PADDING;
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  }

  let top =
    spaceBelow >= menuHeight || spaceBelow >= spaceAbove
      ? rect.bottom + 6
      : rect.top - menuHeight - 6;

  if (top + menuHeight > window.innerHeight - VIEWPORT_PADDING) {
    top = window.innerHeight - menuHeight - VIEWPORT_PADDING;
  }
  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING;
  }

  return { top, left, ready: true };
}

const DropdownMenu = ({ items, show, onClose, trigger, align = "start", zIndex = 1400 }) => {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    ready: false,
  });

  const updatePosition = useCallback(() => {
    if (!show) return false;

    const triggerEl = resolveTriggerEl(trigger);
    if (!triggerEl) return false;

    const measuredHeight =
      dropdownRef.current?.offsetHeight || MENU_ESTIMATED_HEIGHT;
    const measuredWidth =
      dropdownRef.current?.offsetWidth || MENU_WIDTH;

    const next = computeMenuPosition(
      triggerEl,
      align,
      measuredWidth,
      measuredHeight,
    );
    setPosition(next);
    return next.ready;
  }, [show, trigger, align]);

  useLayoutEffect(() => {
    if (!show) {
      setPosition({ top: 0, left: 0, ready: false });
      return undefined;
    }

    updatePosition();
    const raf1 = window.requestAnimationFrame(() => {
      updatePosition();
      window.requestAnimationFrame(() => updatePosition());
    });

    return () => window.cancelAnimationFrame(raf1);
  }, [show, updatePosition, items]);

  useEffect(() => {
    if (!show) return undefined;

    const handleReposition = () => updatePosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [show, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const triggerEl = resolveTriggerEl(trigger);
      if (
        show &&
        !dropdownRef.current?.contains(e.target) &&
        !triggerEl?.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [show, onClose, trigger]);

  if (!show) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="app-dropdown-menu dropdown-menu show shadow-sm"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex,
        visibility: position.ready ? "visible" : "hidden",
        pointerEvents: position.ready ? "auto" : "none",
      }}
    >
      {items.map((item, idx) =>
        item.divider ? (
          <div key={idx} className="dropdown-divider" />
        ) : (
          <button
            key={idx}
            type="button"
            className="dropdown-item d-flex align-items-center"
            data-tour={item.dataTour || undefined}
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
            title={typeof item.label === "string" ? item.label : ""}
          >
            {item.icon ? (
              <i
                className={`${item.icon} me-2 flex-shrink-0`}
                aria-hidden="true"
              />
            ) : null}
            <span className="dropdown-item-label">{item.label}</span>
          </button>
        ),
      )}
    </div>,
    document.body,
  );
};

export default DropdownMenu;
